import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GitCompare, UserPlus, UserMinus, FilePenLine, RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABEL = {
  no_kk: "No. KK",
  nik: "NIK",
  nama: "Nama",
  tempat_lahir: "Tempat Lahir",
  tanggal_lahir: "Tanggal Lahir",
  status_perkawinan: "Status Kawin",
  jenis_kelamin: "Jenis Kelamin",
  alamat: "Alamat",
  rt: "RT",
  rw: "RW",
  dusun: "Dusun",
  tps: "TPS",
  disabilitas: "Disabilitas",
  keterangan: "Keterangan",
  status: "Status",
  alasan: "Alasan PPDP",
  petugas: "Petugas",
};

const STATUS_LABEL = {
  ACTIVE: "Aktif",
  MENINGGAL: "Meninggal",
  PINDAH_DOMISILI: "Pindah Domisili",
  DATA_GANDA: "Data Ganda",
  PERLU_VERIFIKASI: "Perlu Verifikasi",
  RUMAH_KOSONG: "Rumah Kosong",
};

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 grid place-items-center rounded-md ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-bold tabular-nums">{(value ?? 0).toLocaleString("id-ID")}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DpsDeltaPage() {
  const [dusuns, setDusuns] = useState([]);
  const [tpsList, setTpsList] = useState([]);
  const [scope, setScope] = useState({ dusun: "", tps: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("ringkasan");
  const [downloading, setDownloading] = useState(null);
  const PAGE_SIZE = 25;
  const [offsets, setOffsets] = useState({ tambahan: 0, pengurangan: 0, perubahan: 0 });

  useEffect(() => {
    api.get("/dusuns").then((r) => setDusuns(r.data)).catch(() => {});
    api.get("/tps").then((r) => setTpsList(r.data)).catch(() => {});
  }, []);

  const filteredTps = useMemo(() => {
    if (!scope.dusun) return tpsList;
    return tpsList.filter((t) => t.dusun === scope.dusun);
  }, [tpsList, scope.dusun]);

  const fetchDelta = async (resetOffsets = true) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE };
      const off = resetOffsets ? { tambahan: 0, pengurangan: 0, perubahan: 0 } : offsets;
      params.offset_tambahan = off.tambahan;
      params.offset_pengurangan = off.pengurangan;
      params.offset_perubahan = off.perubahan;
      if (scope.dusun) params.dusun = scope.dusun;
      if (scope.tps) params.tps = scope.tps;
      const { data } = await api.get("/dps-delta", { params });
      setData(data);
      if (resetOffsets) setOffsets({ tambahan: 0, pengurangan: 0, perubahan: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const movePage = async (key, delta) => {
    const newOffset = Math.max(0, (offsets[key] || 0) + delta * PAGE_SIZE);
    setOffsets({ ...offsets, [key]: newOffset });
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE };
      params.offset_tambahan = key === "tambahan" ? newOffset : offsets.tambahan;
      params.offset_pengurangan = key === "pengurangan" ? newOffset : offsets.pengurangan;
      params.offset_perubahan = key === "perubahan" ? newOffset : offsets.perubahan;
      if (scope.dusun) params.dusun = scope.dusun;
      if (scope.tps) params.tps = scope.tps;
      const { data } = await api.get("/dps-delta", { params });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // initial global delta on mount
    api.get("/dps-delta", { params: { limit: PAGE_SIZE } }).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadExcel = async (category) => {
    setDownloading(category);
    try {
      const params = {};
      if (scope.dusun) params.dusun = scope.dusun;
      if (scope.tps) params.tps = scope.tps;
      params.category = category;
      const res = await api.get("/dps-delta/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `Delta_DPS_${category}.xlsx`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success(`File ${category} berhasil diunduh`);
    } catch (err) {
      let msg = formatApiError(err);
      try {
        const data = err?.response?.data;
        if (data && typeof data.text === "function") {
          const txt = await data.text();
          try {
            const parsed = JSON.parse(txt);
            if (parsed?.detail) msg = typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
          } catch { /* not JSON */ }
        }
      } catch { /* noop */ }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Laporan Perubahan</p>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <GitCompare className="h-7 w-7" /> Delta DPS → DPSHP
        </h1>
        <p className="text-sm text-slate-600 mt-1">Perbandingan data pemilih sekarang terhadap baseline DPS asli, per area.</p>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="pt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={scope.dusun || "all"} onValueChange={(v) => setScope({ dusun: v === "all" ? "" : v, tps: "" })}>
            <SelectTrigger className="h-11" data-testid="filter-dusun"><SelectValue placeholder="Semua Dusun" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Dusun</SelectItem>
              {dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scope.tps || "all"} onValueChange={(v) => setScope({ ...scope, tps: v === "all" ? "" : v })}>
            <SelectTrigger className="h-11" data-testid="filter-tps"><SelectValue placeholder="Semua TPS" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua TPS</SelectItem>
              {filteredTps.map((t) => <SelectItem key={t.id} value={t.kode}>{t.kode} {t.dusun ? `(${t.dusun})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={fetchDelta} disabled={loading} className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="apply-filter-button">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Memuat..." : "Terapkan"}
          </Button>
          <div className="text-xs text-slate-500 self-center">
            {data?.scope?.dusun || data?.scope?.tps ? (
              <>Area: <b>{data.scope.dusun || "Semua"}</b>{data.scope.tps ? ` · ${data.scope.tps}` : ""}</>
            ) : "Area: Seluruh desa"}
          </div>
        </CardContent>
      </Card>

      {/* Stats — semua APPROVED (resmi masuk DPSHP) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="delta-stats">
        <Stat icon={GitCompare} label="Baseline DPS Aktif" value={data?.baseline_aktif_count} color="bg-slate-800" testid="stat-baseline" />
        <Stat icon={UserPlus} label="Tambahan (Approved)" value={data?.tambahan_count} color="bg-emerald-700" testid="stat-tambahan" />
        <Stat icon={UserMinus} label="Pengurangan (Approved)" value={data?.pengurangan_count} color="bg-red-700" testid="stat-pengurangan" />
        <Stat icon={FilePenLine} label="Perubahan (Approved)" value={data?.perubahan_count} color="bg-amber-700" testid="stat-perubahan" />
        <Stat icon={GitCompare} label="Total DPSHP" value={data?.dpshp_total_count} color="bg-blue-900" testid="stat-dpshp" />
      </div>

      {/* Alert PENDING BARU (BARU status=PENDING, belum dibuat voter) */}
      {(data?.tambahan_pending_count ?? 0) > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-3" data-testid="pending-baru-alert">
          <UserPlus className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">
              {data.tambahan_pending_count.toLocaleString("id-ID")} pemilih baru menunggu verifikasi Panitia
            </div>
            <div className="text-amber-800 text-xs mt-0.5">
              Belum dibuat di DPS. Verifikasi di halaman <b>Log Perubahan PPDP</b> (filter Status = Menunggu Persetujuan) untuk menyetujui atau menolak.
            </div>
          </div>
        </div>
      )}

      {/* Alert APPLIED belum di-review Panitia (UBAH/STATUS/TAMBAH direct-apply) */}
      {((data?.tambahan_review_count ?? 0) + (data?.pengurangan_review_count ?? 0) + (data?.perubahan_review_count ?? 0)) > 0 && (
        <div className="rounded-md border border-blue-300 bg-blue-50 p-3 flex items-start gap-3" data-testid="pending-review-alert">
          <FilePenLine className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-blue-900">
              Perubahan PPDP menunggu review Panitia — belum resmi masuk DPSHP
            </div>
            <div className="text-blue-800 text-xs mt-0.5 space-x-3">
              {(data.tambahan_review_count ?? 0) > 0 && <span>Tambahan: <b>{data.tambahan_review_count}</b></span>}
              {(data.pengurangan_review_count ?? 0) > 0 && <span>Pengurangan/TMS: <b>{data.pengurangan_review_count}</b></span>}
              {(data.perubahan_review_count ?? 0) > 0 && <span>Ubah Data: <b>{data.perubahan_review_count}</b></span>}
              <span className="italic">· buka <b>Log Perubahan PPDP</b> untuk Setujui / Kembalikan / Tolak</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for details */}
      <Card>
        <CardHeader>
          <div className="flex gap-2 flex-wrap">
            {[
              ["ringkasan", "Ringkasan"],
              ["tambahan", `Tambahan (${data?.tambahan_count ?? 0})`],
              ["pengurangan", `Pengurangan (${data?.pengurangan_count ?? 0})`],
              ["perubahan", `Perubahan (${data?.perubahan_count ?? 0})`],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`tab-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {tab === "ringkasan" && (
            <div className="text-sm text-slate-700 space-y-2">
              <p>Baseline DPS dari impor awal: <b className="tabular-nums">{((data?.baseline_aktif_count ?? 0) + (data?.pengurangan_count ?? 0)).toLocaleString("id-ID")}</b> pemilih.</p>
              <p>Setelah verifikasi Panitia (approved): <b className="text-emerald-700">{data?.tambahan_count ?? 0} tambahan</b>, <b className="text-red-700">{data?.pengurangan_count ?? 0} pengurangan (TMS)</b>, dan <b className="text-amber-700">{data?.perubahan_count ?? 0} perubahan data</b>.</p>
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-blue-900 text-xs">
                <div className="font-semibold text-sm mb-1">Formula DPSHP</div>
                <div className="tabular-nums">
                  Baseline DPS Aktif ({(data?.baseline_aktif_count ?? 0).toLocaleString("id-ID")})
                  {" + "}Tambahan Approved ({data?.tambahan_count ?? 0})
                </div>
                <div className="text-slate-600 text-[11px]">Pengurangan approved sudah dikurangkan dari baseline aktif</div>
                <div className="mt-1 font-bold">= <span className="text-blue-900">{(data?.dpshp_total_count ?? 0).toLocaleString("id-ID")} pemilih di DPSHP</span></div>
              </div>
              {(data?.tambahan_pending_count ?? 0) > 0 && (
                <p className="text-amber-800">
                  <b>{data.tambahan_pending_count}</b> pemilih baru <b>menunggu verifikasi Panitia</b> — belum dibuat di DPS.
                </p>
              )}
              <p className="text-xs text-slate-500 pt-2">Klik tab di atas untuk melihat detail per kategori.</p>

              {/* Download Excel bar */}
              <div className="mt-5 pt-4 border-t border-slate-200" data-testid="delta-excel-download-bar">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Unduh File DPT</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Setiap kategori diunduh sebagai file Excel terpisah, mengikuti filter area yang aktif.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { key: "baseline", label: "Baseline DPS Aktif", count: data?.baseline_aktif_count, color: "border-slate-300 hover:bg-slate-50" },
                    { key: "tambahan", label: "Tambahan", count: data?.tambahan_count, color: "border-emerald-300 hover:bg-emerald-50 text-emerald-800" },
                    { key: "pengurangan", label: "Pengurangan", count: data?.pengurangan_count, color: "border-red-300 hover:bg-red-50 text-red-800" },
                    { key: "perubahan", label: "Perubahan", count: data?.perubahan_count, color: "border-amber-300 hover:bg-amber-50 text-amber-900" },
                  ].map((b) => (
                    <Button
                      key={b.key}
                      variant="outline"
                      onClick={() => downloadExcel(b.key)}
                      disabled={downloading !== null}
                      className={`h-auto py-3 px-3 flex items-start gap-2 justify-start ${b.color}`}
                      data-testid={`download-excel-${b.key}`}
                    >
                      <FileDown className={`h-4 w-4 mt-0.5 ${downloading === b.key ? "animate-pulse" : ""}`} />
                      <div className="flex-1 text-left">
                        <div className="text-sm font-semibold">{b.label}</div>
                        <div className="text-[11px] font-normal text-slate-500">
                          {downloading === b.key ? "Menyiapkan file..." : `${(b.count ?? 0).toLocaleString("id-ID")} baris · .xlsx`}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "tambahan" && (
            <>
              <div className="flex justify-end mb-2">
                <Button size="sm" variant="outline" onClick={() => downloadExcel("tambahan")} disabled={downloading !== null} data-testid="tab-download-tambahan">
                  <FileDown className={`h-4 w-4 mr-1.5 ${downloading === "tambahan" ? "animate-pulse" : ""}`} />
                  {downloading === "tambahan" ? "Menyiapkan..." : "Download Excel"}
                </Button>
              </div>
              <SimpleTable
                items={data?.tambahan}
                columns={["nik", "nama", "jenis_kelamin", "tps", "rt", "rw", "dusun", "keterangan", "alasan", "petugas"]}
                emptyMsg="Belum ada penambahan pemilih."
                testid="tambahan-table"
              />
              <Pager total={data?.tambahan_count} offset={offsets.tambahan} pageSize={PAGE_SIZE} onMove={(d) => movePage("tambahan", d)} testid="pager-tambahan" />
            </>
          )}

          {tab === "pengurangan" && (
            <>
              <div className="flex justify-end mb-2">
                <Button size="sm" variant="outline" onClick={() => downloadExcel("pengurangan")} disabled={downloading !== null} data-testid="tab-download-pengurangan">
                  <FileDown className={`h-4 w-4 mr-1.5 ${downloading === "pengurangan" ? "animate-pulse" : ""}`} />
                  {downloading === "pengurangan" ? "Menyiapkan..." : "Download Excel"}
                </Button>
              </div>
              <SimpleTable
                items={data?.pengurangan}
                columns={["nik", "nama", "tps", "status", "alasan", "petugas"]}
                emptyMsg="Belum ada pengurangan pemilih."
                testid="pengurangan-table"
                renderStatus
                emphasizeAlasan
              />
              <Pager total={data?.pengurangan_count} offset={offsets.pengurangan} pageSize={PAGE_SIZE} onMove={(d) => movePage("pengurangan", d)} testid="pager-pengurangan" />
            </>
          )}

          {tab === "perubahan" && (
            <div className="space-y-3" data-testid="perubahan-list">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => downloadExcel("perubahan")} disabled={downloading !== null} data-testid="tab-download-perubahan">
                  <FileDown className={`h-4 w-4 mr-1.5 ${downloading === "perubahan" ? "animate-pulse" : ""}`} />
                  {downloading === "perubahan" ? "Menyiapkan..." : "Download Excel"}
                </Button>
              </div>
              {(!data?.perubahan || data.perubahan.length === 0) && <p className="text-sm text-slate-500 italic py-4">Belum ada perubahan field pada data DPS.</p>}
              {(data?.perubahan || []).map((p) => (
                <div key={p.id} className="rounded-md border border-amber-200 bg-amber-50/40 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                    <div className="font-semibold">{p.nama} <span className="font-mono text-xs text-slate-500">· {p.nik}</span></div>
                    <Badge variant="outline" className="text-amber-800 border-amber-300">{p.dusun} · {p.tps}</Badge>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      <tr><th className="text-left py-1">Field</th><th className="text-left py-1">DPS Asli</th><th className="text-left py-1">Sekarang</th></tr>
                    </thead>
                    <tbody>
                      {p.diffs.map((d, i) => (
                        <tr key={i} className="border-t border-amber-100">
                          <td className="py-1.5 font-medium">{FIELD_LABEL[d.field] || d.field}</td>
                          <td className="py-1.5 text-slate-600 line-through">{formatValue(d.field, d.lama)}</td>
                          <td className="py-1.5 font-semibold text-amber-900">{formatValue(d.field, d.baru)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(p.alasan || p.petugas) && (
                    <div className="mt-3 pt-3 border-t border-amber-200 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div className="sm:col-span-2">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Alasan PPDP</span>
                        <div className="text-slate-800">{p.alasan || <span className="italic text-slate-400">—</span>}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Petugas</span>
                        <div className="text-slate-800">{p.petugas || <span className="italic text-slate-400">—</span>}{p.bukti_count > 0 && <span className="ml-1 text-emerald-700">· {p.bukti_count} bukti</span>}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <Pager total={data?.perubahan_count} offset={offsets.perubahan} pageSize={PAGE_SIZE} onMove={(d) => movePage("perubahan", d)} testid="pager-perubahan" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatValue(field, v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-400 italic">-</span>;
  if (field === "status") return STATUS_LABEL[v] || v;
  return String(v);
}

function Pager({ total, offset, pageSize, onMove, testid }) {
  if (!total || total <= pageSize) return null;
  const from = offset + 1;
  const to = Math.min(offset + pageSize, total);
  const hasPrev = offset > 0;
  const hasNext = to < total;
  return (
    <div className="flex items-center justify-between mt-3 text-sm" data-testid={testid}>
      <div className="text-slate-600">Menampilkan <b>{from}–{to}</b> dari <b>{total.toLocaleString("id-ID")}</b></div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={!hasPrev} onClick={() => onMove(-1)} data-testid={`${testid}-prev`}>← Sebelumnya</Button>
        <Button size="sm" variant="outline" disabled={!hasNext} onClick={() => onMove(1)} data-testid={`${testid}-next`}>Berikutnya →</Button>
      </div>
    </div>
  );
}

function SimpleTable({ items, columns, emptyMsg, renderStatus, emphasizeAlasan, testid }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-500 italic py-4">{emptyMsg}</p>;
  }
  return (
    <div className="overflow-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          <tr>{columns.map((c) => (
            <th key={c} className={`text-left px-3 py-2 ${emphasizeAlasan && c === "alasan" ? "bg-red-50 text-red-800" : ""}`}>
              {FIELD_LABEL[c] || c}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {items.map((v) => (
            <tr key={v.id} className="border-t border-slate-200 align-top">
              {columns.map((c) => {
                let cell;
                if (c === "status" && renderStatus) cell = STATUS_LABEL[v[c]] || v[c];
                else if (c === "nik") cell = <span className="font-mono text-xs">{v[c]}</span>;
                else if (c === "jenis_kelamin") cell = v[c] === "L" ? "Laki-laki" : "Perempuan";
                else if (c === "alasan") cell = v[c] || <span className="italic text-slate-400">—</span>;
                else if (c === "petugas") cell = (
                  <span className="text-xs">
                    {v[c] || <span className="italic text-slate-400">—</span>}
                    {v.bukti_count > 0 && <span className="ml-1 text-emerald-700">· {v.bukti_count} bukti</span>}
                  </span>
                );
                else cell = v[c] ?? "-";
                return (
                  <td key={c} className={`px-3 py-2 ${emphasizeAlasan && c === "alasan" ? "bg-red-50/40 font-medium text-red-900" : ""}`}>
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
